import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Bausteine der Ertragsausfall-Vertragsmaske. Optik folgt den bestehenden
 * Vertragsmasken (KFZ / Immobilie / Allgemein); ergänzt sind die Meta-Zeilen
 * je Feld (Datenquelle, Prüfhinweis, Pflichtlogik) aus der Felddefinition.
 */
export const useEaStyles = makeStyles({
  stepBody: {
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
    "@media (min-width: 860px)": { gridTemplateColumns: "1fr 1fr" },
  },
  fieldSpan2: { "@media (min-width: 860px)": { gridColumnStart: "span 2" } },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    backgroundColor: BRAND.white,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  labelRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.ink,
    lineHeight: 1.3,
  },
  /** Spalte F der Felddefinition — Pflicht / Logik. */
  reqBadge: {
    flexShrink: 0,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#f3f0f5",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("999px"),
  },
  reqBadgePflicht: {
    color: BRAND.purple,
    backgroundColor: "#f3e9f5",
  },
  reqBadgeAuto: {
    color: "#1d5c3c",
    backgroundColor: "#e6f2ea",
  },
  /** Spalte D — Datenquelle / DSI-Bezug. */
  sourceRow: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    flexWrap: "wrap",
  },
  dsiChip: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.7rem",
    color: BRAND.ink,
    backgroundColor: "#f3f0f5",
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: "6px",
    paddingRight: "6px",
    ...shorthands.borderRadius("4px"),
  },
  /** Spalte E — Prüf-/Abgleichhinweis. */
  checkRow: {
    fontSize: "0.78rem",
    color: "#7a5a10",
    backgroundColor: "#fdf6e6",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
  },
  hint: { fontSize: "0.76rem", color: BRAND.muted },
  input: {
    boxSizing: "border-box",
    width: "100%",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "11px",
    paddingRight: "11px",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("8px"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  textarea: { minHeight: "88px", resize: "vertical" },
  select: {
    appearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='%235d5664' d='M6 8L0 0h12L6 8z'/%3e%3c/svg%3e\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "32px",
    cursor: "pointer",
  },
  inputWrap: { position: "relative", display: "block" },
  unitSuffix: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.85rem",
    color: BRAND.muted,
    pointerEvents: "none",
  },
  inputWithUnit: { paddingRight: "48px" },
  // Ja/Nein
  ynRow: { display: "flex", columnGap: "8px" },
  ynBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    minWidth: "84px",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1.5px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "border-color, background-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  ynJa: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarGreen),
  },
  ynNein: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  // Teilfelder eines Deckungsbausteins
  partGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    marginTop: "4px",
    paddingTop: "12px",
    ...shorthands.borderTop("1px", "dashed", BRAND.border),
    "@media (min-width: 640px)": { gridTemplateColumns: "1fr 1fr" },
  },
  partSpan2: { "@media (min-width: 640px)": { gridColumnStart: "span 2" } },
  partField: { display: "flex", flexDirection: "column", rowGap: "5px" },
  partLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  // Chips / Tags
  tagRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.82rem",
    color: BRAND.ink,
    backgroundColor: "#f3e9f5",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "6px",
    ...shorthands.borderRadius("999px"),
  },
  tagRemove: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.border("0"),
    color: BRAND.purple,
    fontSize: "0.9rem",
    lineHeight: 1,
    paddingLeft: "2px",
    paddingRight: "2px",
  },
  // Platzhalter für Dokumentverknüpfung
  docBox: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    fontSize: "0.85rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "dashed", BRAND.border),
    ...shorthands.borderRadius("8px"),
  },
  // Auswahl-Kacheln (Betriebsstätten)
  pickList: { display: "flex", flexDirection: "column", rowGap: "8px" },
  pickTile: {
    cursor: "pointer",
    appearance: "none",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "11px",
    paddingBottom: "11px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  pickTileActive: {
    ...shorthands.border("2px", "solid", BRAND.purple),
    backgroundColor: "#f9f2fa",
  },
  pickMark: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    flexShrink: 0,
    fontSize: "0.7rem",
    color: BRAND.white,
    backgroundColor: BRAND.border,
    ...shorthands.borderRadius("4px"),
  },
  pickMarkActive: { backgroundColor: BRAND.purple },
  pickName: { fontSize: "0.92rem", fontWeight: 600 },
  pickMeta: { fontSize: "0.78rem", color: BRAND.muted },
  emptyNote: {
    fontSize: "0.86rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.border),
  },
});
